import { motion } from 'framer-motion';
import { Sparkles, Zap } from 'lucide-react';
import { useState } from 'react';
import DaysSelector from '@/components/training/DaysSelector';
import EquipmentSelector from '@/components/training/EquipmentSelector';
import LevelSelector from '@/components/training/LevelSelector';
import SportSelector from '@/components/training/SportSelector';
import { useTraining } from '@/components/training/TrainingContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useGenerateRoutine } from '@/hooks/useGenerateRoutine';

export default function TrainingForm() {
  const { setGeneratedRoutine } = useTraining();
  const { generate, isLoading: isGenerating, error } = useGenerateRoutine();

  const [formData, setFormData] = useState({
    sport: 'general',
    days: 3,
    level: 'intermediate',
    equipment: ['bodyweight'],
  });

  const handleGenerate = () => {
    generate(formData).then((routine) => {
      if (routine) {
        setGeneratedRoutine(routine);
      }
    });
  };

  const updateField = (field: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
    >
      <Card className="overflow-hidden border-0 shadow-2xl shadow-slate-200/50">
        {/* Gradient Header */}
        <div className="bg-gradient-to-r from-violet-600 via-indigo-600 to-purple-600 p-6 sm:p-8">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
              <Zap className="w-8 h-8" />
              Training Routine Generator
            </h1>
            <p className="text-white/80 mt-2">Create your personalized workout plan in seconds</p>
          </motion.div>
        </div>

        <CardContent className="p-6 sm:p-8 space-y-8">
          <SportSelector value={formData.sport} onChange={(v: string) => updateField('sport', v)} />

          <DaysSelector value={formData.days} onChange={(v: number) => updateField('days', v)} />

          <LevelSelector value={formData.level} onChange={(v: string) => updateField('level', v)} />

          <EquipmentSelector
            value={formData.equipment}
            onChange={(v: string[]) => updateField('equipment', v)}
          />

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error.message}
            </div>
          )}

          {/* Generate Button */}
          <motion.div whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
            <Button
              onClick={handleGenerate}
              disabled={isGenerating}
              className={`
                w-full h-14 text-lg font-semibold rounded-2xl
                bg-gradient-to-r from-violet-600 to-indigo-600
                hover:from-violet-700 hover:to-indigo-700
                text-white border-0 shadow-lg shadow-violet-200
                transition-all duration-300
              `}
            >
              {isGenerating ? (
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                  className="mr-2"
                >
                  <Sparkles className="w-5 h-5" />
                </motion.div>
              ) : (
                <Sparkles className="w-5 h-5 mr-2" />
              )}
              {isGenerating ? 'Generating...' : 'Generate Routine'}
            </Button>
          </motion.div>
        </CardContent>
      </Card>
    </motion.div>
  );
}
